import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, BookOpen, CheckCircle, Clock, ArrowUpRight, ArrowDownRight,
  TrendingUp, Shield, Calendar, ChevronRight, Play, AlertTriangle,
  Loader2, RefreshCw, AlertCircle, BookMarked, Target, X, Search,
  Info, HelpCircle, Mail, Building2, Briefcase, ExternalLink, ChevronDown,
  Download, UserPlus, Megaphone, FileText, BarChart3, Send
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Area, AreaChart, CartesianGrid, Legend, LineChart, Line
} from "recharts";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../../components/ui/tooltip";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } } };
const PIE_COLORS = ["#10b981", "#f59e0b", "#3b82f6", "#ef4444"];

/* -------- Reusable info tooltip (i) -------- */
const InfoTip = ({ text }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="w-3.5 h-3.5 text-slate-300 hover:text-slate-500 cursor-help inline-block ml-1 shrink-0" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs bg-slate-900 text-white border-0">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/* -------- Calculation/formula tooltip (?) -------- */
const CalcTip = ({ text }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-3.5 h-3.5 text-slate-300 hover:text-blue-500 cursor-help inline-block ml-1 shrink-0" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs bg-blue-900 text-white border-0">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/* -------- HubSpot-style slide-out panel -------- */
const SlidePanel = ({ open, onClose, title, subtitle, children }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/20 z-40" onClick={onClose}
        />
        <motion.div
          initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="fixed top-0 right-0 bottom-0 w-full max-w-lg bg-white z-50 shadow-2xl flex flex-col border-l border-slate-200"
          data-testid="slide-panel"
        >
          <div className="flex items-start justify-between p-5 border-b border-slate-100">
            <div>
              <h2 className="text-lg font-extrabold text-slate-900">{title}</h2>
              {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5">{children}</div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const TenantAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("all");

  // Panel states
  const [panel, setPanel] = useState(null); // { type, title, subtitle }
  const [panelSearch, setPanelSearch] = useState("");

  useEffect(() => { fetchDashboard(); }, [dateRange]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const params = dateRange !== "all" ? `?days=${dateRange}` : "";
      const res = await api.get(`/admin/dashboard${params}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openPanel = (type, title, subtitle) => {
    setPanel({ type, title, subtitle });
    setPanelSearch("");
  };

  if (loading || !data) {
    return (
      <AdminLayout title="Dashboard" variant="tenant_admin">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  const s = data.stats;
  const stats = [
    { key: "learners", title: "Total Learners", value: s.total_learners, sub: `${s.active_learners} active`, trend: "up", icon: Users, color: "#3b82f6", bg: "rgba(59,130,246,0.08)", tip: "Total registered learners in your organization" },
    { key: "active_learners", title: "Active Learners", value: s.active_learners, sub: `of ${s.total_learners} total`, trend: "up", icon: Users, color: "#8b5cf6", bg: "rgba(139,92,246,0.08)", tip: "Learners with status 'active' who are currently enabled in the system. Active = account enabled & not suspended." },
    { key: "completion", title: "Completion Rate", value: `${s.completion_rate}%`, sub: `${s.completed} of ${s.total_enrollments}`, trend: s.completion_rate >= 50 ? "up" : "down", icon: CheckCircle, color: "#10b981", bg: "rgba(16,185,129,0.08)", tip: "Percentage of enrollments completed vs total assigned. Formula: (Completed Enrollments / Total Enrollments) x 100" },
    { key: "in_progress", title: "In Progress", value: s.in_progress, sub: `${s.not_started || 0} not started`, trend: "up", icon: Clock, color: "#f59e0b", bg: "rgba(245,158,11,0.08)", tip: "Courses currently being worked on by learners" },
  ];

  const compPie = [
    { name: "Completed", value: data.compliance_status.completed },
    { name: "In Progress", value: data.compliance_status.in_progress },
    { name: "Pending", value: data.compliance_status.pending },
    { name: "Overdue", value: data.compliance_status.overdue },
  ].filter(d => d.value > 0);

  /* Panel content renderers */
  const renderPanelContent = () => {
    if (!panel) return null;
    const q = panelSearch.toLowerCase();

    if (panel.type === "learners") {
      const list = (data.learner_details || []).filter(l => !q || l.full_name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.department?.toLowerCase().includes(q));
      return (
        <div className="space-y-3">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search learners..." value={panelSearch} onChange={e => setPanelSearch(e.target.value)} className="pl-9 rounded-xl border-slate-200" />
          </div>
          <p className="text-xs text-slate-400">{list.length} learners</p>
          {list.map((l, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all cursor-pointer" onClick={() => { setPanel(null); navigate(l.id ? `/tenant/team/${l.id}` : `/tenant/team`); }} data-testid={`panel-learner-${i}`}>
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-xs font-bold text-blue-700">
                {l.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{l.full_name}</p>
                <p className="text-xs text-slate-400 truncate">{l.email}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-semibold text-slate-600">{l.completed_count}/{l.enrolled_count}</p>
                <p className="text-[10px] text-slate-400">{l.department || "No dept"}</p>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            </div>
          ))}
        </div>
      );
    }

    if (panel.type === "courses") {
      const list = (data.course_details || []).filter(c => !q || c.title?.toLowerCase().includes(q) || c.category?.toLowerCase().includes(q));
      return (
        <div className="space-y-3">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search courses..." value={panelSearch} onChange={e => setPanelSearch(e.target.value)} className="pl-9 rounded-xl border-slate-200" />
          </div>
          <p className="text-xs text-slate-400">{list.length} courses</p>
          {list.map((c, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all cursor-pointer" onClick={() => { setPanel(null); navigate(c.id ? `/course/${c.id}` : `/tenant/courses`); }} data-testid={`panel-course-${i}`}>
              <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center"><BookOpen className="w-4 h-4 text-violet-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{c.title}</p>
                <p className="text-xs text-slate-400">{c.category} &middot; {c.course_type} &middot; {c.duration_hours}h</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold text-emerald-600">{c.completed}/{c.enrolled}</p>
                <p className="text-[10px] text-slate-400">completed</p>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            </div>
          ))}
        </div>
      );
    }

    if (panel.type === "compliance") {
      const list = (data.compliance_details || []).filter(c => {
        if (panel.filter === "overdue") return c.is_overdue;
        if (panel.filter === "pending") return c.status === "pending";
        if (panel.filter === "completed") return c.status === "completed";
        return !q || c.user_name?.toLowerCase().includes(q) || c.template_title?.toLowerCase().includes(q);
      }).filter(c => !q || c.user_name?.toLowerCase().includes(q) || c.template_title?.toLowerCase().includes(q));
      return (
        <div className="space-y-3">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search compliance..." value={panelSearch} onChange={e => setPanelSearch(e.target.value)} className="pl-9 rounded-xl border-slate-200" />
          </div>
          <p className="text-xs text-slate-400">{list.length} assignments</p>
          {list.map((c, i) => (
            <div key={i} className={`p-3.5 rounded-xl border transition-all ${c.is_overdue ? "border-red-200 bg-red-50/30" : "border-slate-100 hover:border-slate-200"}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-slate-800">{c.user_name}</p>
                <span className={`ent-status ent-status-${c.is_overdue ? "overdue" : c.status}`}>
                  <span className={`ent-status-dot ent-status-dot-${c.is_overdue ? "overdue" : c.status}`} />
                  {c.is_overdue ? "Overdue" : c.status === "in_progress" ? "In Progress" : c.status?.charAt(0).toUpperCase() + c.status?.slice(1)}
                </span>
              </div>
              <p className="text-xs text-slate-500">{c.template_title}</p>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
                <span><Mail className="w-3 h-3 inline mr-1" />{c.user_email}</span>
                {c.due_date && <span><Calendar className="w-3 h-3 inline mr-1" />Due: {c.due_date}</span>}
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No matching records</p>}
        </div>
      );
    }

    if (panel.type === "action") {
      const ai = panel.actionItem;
      return (
        <div className="space-y-3">
          <div className={`p-4 rounded-xl border ${ai?.severity === "critical" ? "bg-red-50 border-red-200" : ai?.severity === "warning" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`}>
            <p className="font-semibold text-slate-800 mb-1">{ai?.title}</p>
            <p className="text-sm text-slate-600">{ai?.description}</p>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Affected ({ai?.affected?.length || 0})</p>
          {ai?.affected?.map((a, i) => (
            <div key={i} className="p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-all">
              <p className="text-sm font-semibold text-slate-800">{a.name}</p>
              <p className="text-xs text-slate-400">{a.course || a.compliance || ""} {a.due_date ? `· Due: ${a.due_date}` : ""} {a.progress !== undefined ? `· ${a.progress}% progress` : ""}</p>
            </div>
          ))}
        </div>
      );
    }

    if (panel.type === "session") {
      const sess = panel.session;
      return (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-violet-50 border border-violet-100">
            <h3 className="font-bold text-slate-900">{sess?.title}</h3>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
              <span><Calendar className="w-3 h-3 inline mr-1" />{sess?.session_date} {sess?.start_time && `at ${sess.start_time}`}{sess?.end_time && ` - ${sess.end_time}`}</span>
              {sess?.location && <span><Building2 className="w-3 h-3 inline mr-1" />{sess.location}</span>}
              {sess?.instructor_name && <span><Briefcase className="w-3 h-3 inline mr-1" />{sess.instructor_name}</span>}
            </div>
            {sess?.description && <p className="text-sm text-slate-500 mt-2">{sess.description}</p>}
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Registered Attendees ({sess?.attendees?.length || 0})</p>
          {sess?.attendees?.map((a, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100">
              <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center text-[10px] font-bold text-violet-700">
                {a.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{a.name}</p>
                <p className="text-xs text-slate-400">{a.email} {a.department && `· ${a.department}`}</p>
              </div>
            </div>
          ))}
          {(!sess?.attendees || sess.attendees.length === 0) && <p className="text-sm text-slate-400 text-center py-4">No registrations yet</p>}
        </div>
      );
    }

    if (panel.type === "category") {
      const cat = panel.category;
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-center">
              <p className="text-xl font-extrabold text-blue-600">{cat?.enrolled}</p><p className="text-[10px] text-slate-500">Enrolled</p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-center">
              <p className="text-xl font-extrabold text-emerald-600">{cat?.completed}</p><p className="text-[10px] text-slate-500">Completed</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-100 text-center">
              <p className="text-xl font-extrabold text-amber-600">{cat?.in_progress}</p><p className="text-[10px] text-slate-500">In Progress</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-center">
              <p className="text-xl font-extrabold text-slate-600">{cat?.not_started}</p><p className="text-[10px] text-slate-500">Not Started</p>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Completion Rate</span>
            <span className="font-bold text-blue-600">{cat?.completion_rate}%</span>
          </div>
          <div className="ent-progress-bar"><div className="ent-progress-fill" style={{ width: `${cat?.completion_rate || 0}%` }} /></div>
          <p className="text-xs text-slate-400">{cat?.course_count} unique courses in this category</p>
        </div>
      );
    }

    if (panel.type === "completion") {
      const completed = (data.learner_details || []).filter(l => l.completed_count > 0).sort((a, b) => b.completed_count - a.completed_count);
      return (
        <div className="space-y-3">
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-slate-700">Overall Completion Rate</span>
              <span className="text-2xl font-extrabold text-emerald-600">{s.completion_rate}%</span>
            </div>
            <p className="text-xs text-slate-500">{s.completed} completed out of {s.total_enrollments} total enrollments</p>
            <div className="h-2 bg-emerald-100 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${s.completion_rate}%` }} />
            </div>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Top Completers</p>
          {completed.length > 0 ? completed.slice(0, 15).map((l, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-all"
              onClick={() => { setPanel(null); navigate(l.id ? `/tenant/team/${l.id}` : `/tenant/team`); }}>
              <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-xs font-bold text-emerald-700">
                {l.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{l.full_name}</p>
                <p className="text-xs text-slate-400">{l.department || "No dept"}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold text-emerald-600">{l.completed_count}/{l.enrolled_count}</p>
                <p className="text-[10px] text-slate-400">{l.enrolled_count > 0 ? Math.round(l.completed_count / l.enrolled_count * 100) : 0}%</p>
              </div>
            </div>
          )) : <p className="text-sm text-slate-400 text-center py-8">No completions yet</p>}
        </div>
      );
    }

    if (panel.type === "in_progress") {
      const inProgressList = (data.learner_details || []).filter(l => l.enrolled_count > l.completed_count && l.enrolled_count > 0);
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-100 text-center">
              <p className="text-xl font-extrabold text-amber-600">{s.in_progress}</p>
              <p className="text-[10px] text-slate-500">In Progress</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-center">
              <p className="text-xl font-extrabold text-slate-600">{s.not_started || 0}</p>
              <p className="text-[10px] text-slate-500">Not Started</p>
            </div>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Learners with In-Progress Courses</p>
          {inProgressList.length > 0 ? inProgressList.slice(0, 15).map((l, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-all"
              onClick={() => { setPanel(null); navigate(l.id ? `/tenant/team/${l.id}` : `/tenant/team`); }}>
              <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center text-xs font-bold text-amber-700">
                {l.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{l.full_name}</p>
                <p className="text-xs text-slate-400">{l.email}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold text-amber-600">{l.enrolled_count - l.completed_count} active</p>
                <p className="text-[10px] text-slate-400">{l.completed_count} done</p>
              </div>
            </div>
          )) : <p className="text-sm text-slate-400 text-center py-8">No learners in progress</p>}
        </div>
      );
    }

    if (panel.type === "active_learners") {
      const activeList = (data.learner_details || []).filter(l => l.enrolled_count > 0);
      return (
        <div className="space-y-3">
          <div className="p-4 rounded-xl bg-violet-50 border border-violet-100">
            <p className="text-xs text-slate-500 mb-1">How Active Learners are calculated</p>
            <p className="text-sm text-slate-700">Active Learners = Users with role "learner" whose account status is "active" (not suspended or deactivated). Currently <strong>{s.active_learners}</strong> of <strong>{s.total_learners}</strong> total learners.</p>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Learners ({activeList.length} with enrollments)</p>
          {activeList.slice(0, 15).map((l, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-all"
              onClick={() => { setPanel(null); navigate(l.id ? `/tenant/team/${l.id}` : `/tenant/team`); }}>
              <div className="w-9 h-9 rounded-full bg-violet-50 flex items-center justify-center text-xs font-bold text-violet-700">
                {l.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{l.full_name}</p>
                <p className="text-xs text-slate-400">{l.email}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold text-violet-600">{l.enrolled_count} courses</p>
                <p className="text-[10px] text-slate-400">{l.completed_count} done</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <AdminLayout title="Dashboard" variant="tenant_admin">
      <motion.div className="ent-page space-y-6" variants={container} initial="hidden" animate="show">

        {/* Header + Date Filter */}
        <motion.div variants={item} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">Dashboard</p>
            <h1 className="text-2xl lg:text-3xl font-extrabold tracking-tight text-slate-900">
              Welcome back, {user?.full_name?.split(" ")[0] || "Admin"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[150px] h-9 text-sm rounded-xl border-slate-200" data-testid="dash-date-filter">
                <Calendar className="w-3.5 h-3.5 mr-1.5 text-slate-400" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="30">Last 30 Days</SelectItem>
                <SelectItem value="90">Last 90 Days</SelectItem>
                <SelectItem value="365">Last Year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="rounded-xl border-slate-200 h-9" onClick={async () => {
              try {
                const params = dateRange !== "all" ? `?days=${dateRange}` : "";
                const res = await api.get(`/admin/dashboard/export${params}`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `dashboard_report_${new Date().toISOString().slice(0,10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success("Report downloaded");
              } catch (e) { toast.error("Export failed"); }
            }} data-testid="dash-export-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl border-slate-200 h-9" onClick={fetchDashboard}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" className="ent-btn-primary !h-9" onClick={() => navigate("/tenant/catalogue")}>
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Catalog
            </Button>
          </div>
        </motion.div>

        {/* Stat Cards */}
        <motion.div variants={item} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, idx) => (
            <motion.div key={idx} className="ent-stat group" whileHover={{ y: -4 }}
              onClick={() => openPanel(stat.key, stat.title, `${stat.value} total`)} data-testid={`dash-stat-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="ent-icon-wrap" style={{ background: stat.bg }}>
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <div className="flex items-center gap-1">
                  {stat.sub && (
                    <span className={`flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      stat.trend === "up" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                    }`}>
                      {stat.trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {stat.sub}
                    </span>
                  )}
                  <InfoTip text={stat.tip} />
                </div>
              </div>
              <div className="stat-num text-slate-900">{stat.value}</div>
              <div className="stat-label">{stat.title}</div>
              <ChevronRight className="w-4 h-4 text-slate-300 absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Add Member", icon: UserPlus, path: "/tenant/team", color: "#3b82f6", bg: "bg-blue-50 hover:bg-blue-100" },
            { label: "Access Requests", icon: Send, path: "/tenant/access-requests", color: "#ef4444", bg: "bg-red-50 hover:bg-red-100" },
            { label: "Announce", icon: Megaphone, path: "/tenant/announcements", color: "#8b5cf6", bg: "bg-violet-50 hover:bg-violet-100" },
            { label: "Analytics", icon: BarChart3, path: "/tenant/analytics", color: "#10b981", bg: "bg-emerald-50 hover:bg-emerald-100" },
            { label: "Certificates", icon: FileText, path: "/tenant/certificates", color: "#f59e0b", bg: "bg-amber-50 hover:bg-amber-100" },
          ].map((action, i) => (
            <button key={i} onClick={() => navigate(action.path)}
              className={`flex items-center gap-3 p-3.5 rounded-xl ${action.bg} border border-transparent transition-all group`}
              data-testid={`quick-action-${i}`}>
              <action.icon className="w-5 h-5 shrink-0" style={{ color: action.color }} />
              <span className="text-sm font-semibold text-slate-700">{action.label}</span>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </motion.div>

        {/* Action Items */}
        {data.action_items?.length > 0 && (
          <motion.div variants={item} className="ent-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="ent-icon-wrap" style={{ background: "rgba(239,68,68,0.08)" }}>
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-base font-bold text-slate-900">Action Items</h2>
              <InfoTip text="Items requiring your attention. Click each to see affected employees." />
              <span className="ml-auto text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600">
                {data.action_items.length}
              </span>
            </div>
            <div className="space-y-2">
              {data.action_items.map((ai, idx) => (
                <div key={idx}
                  className={`flex items-center gap-4 p-3.5 rounded-xl border transition-all cursor-pointer hover:shadow-sm group ${
                    ai.severity === "critical" ? "border-red-200 bg-red-50/50 hover:bg-red-50" :
                    ai.severity === "warning" ? "border-amber-200 bg-amber-50/50 hover:bg-amber-50" :
                    "border-blue-200 bg-blue-50/50 hover:bg-blue-50"
                  }`}
                  onClick={() => { setPanel({ type: "action", title: ai.title, subtitle: ai.description, actionItem: ai }); setPanelSearch(""); }}
                  data-testid={`action-item-${idx}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    ai.severity === "critical" ? "bg-red-100" : ai.severity === "warning" ? "bg-amber-100" : "bg-blue-100"
                  }`}>
                    <AlertTriangle className={`w-5 h-5 ${
                      ai.severity === "critical" ? "text-red-600" : ai.severity === "warning" ? "text-amber-600" : "text-blue-600"
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-slate-800">{ai.title}</h4>
                    <p className="text-xs text-slate-500">{ai.description}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full ${
                      ai.severity === "critical" ? "bg-red-100 text-red-700" :
                      ai.severity === "warning" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                    }`}>{ai.severity}</span>
                    {ai.action_url && (
                      <Button variant="outline" size="sm" className="h-7 text-xs rounded-lg border-slate-200"
                        onClick={(e) => { e.stopPropagation(); navigate(ai.action_url); }}
                        data-testid={`action-item-goto-${idx}`}>
                        Go to <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Enrollment Trend */}
        <motion.div variants={item} className="ent-card p-5">
          <div className="flex items-center gap-2 mb-5">
            <div className="ent-icon-wrap" style={{ background: "rgba(99,102,241,0.08)" }}><TrendingUp className="w-5 h-5 text-indigo-500" /></div>
            <h2 className="text-base font-bold text-slate-900">Enrollment & Completion Trend</h2>
            <CalcTip text="Monthly enrollments vs completions over the last 6 months. Enrollments = newly assigned courses that month. Completions = courses completed that month." />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.monthly_trend} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gE" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.12} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.12} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="enrollments" stroke="#3b82f6" fill="url(#gE)" strokeWidth={2} />
              <Area type="monotone" dataKey="completions" stroke="#10b981" fill="url(#gC)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* ============ COMPLIANCE COMMAND CENTER ============ */}
        <motion.div variants={item} className="ent-card p-0 overflow-hidden" data-testid="compliance-command-center">
          {/* Compliance Header with Health Score */}
          <div className="p-5 pb-0 border-b border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="ent-icon-wrap" style={{ background: "rgba(16,185,129,0.08)" }}><Shield className="w-5 h-5 text-emerald-500" /></div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">Compliance Command Center</h2>
                  <p className="text-xs text-slate-400">Mandatory training compliance across your organization</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="rounded-xl border-slate-200 h-8 text-xs" onClick={() => navigate("/tenant/compliances")} data-testid="compliance-view-all-btn">
                View All <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>

            {/* Compliance Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pb-5">
              {(() => {
                const cs = data.compliance_status;
                return [
                  { label: "Compliance Rate", value: `${cs.compliance_rate}%`, color: cs.compliance_rate >= 80 ? "#10b981" : cs.compliance_rate >= 50 ? "#f59e0b" : "#ef4444", bg: cs.compliance_rate >= 80 ? "bg-emerald-50" : cs.compliance_rate >= 50 ? "bg-amber-50" : "bg-red-50" },
                  { label: "Total Assigned", value: cs.total, color: "#3b82f6", bg: "bg-blue-50" },
                  { label: "Completed", value: cs.completed, color: "#10b981", bg: "bg-emerald-50" },
                  { label: "Pending", value: cs.pending, color: "#f59e0b", bg: "bg-amber-50" },
                  { label: "Overdue", value: cs.overdue, color: "#ef4444", bg: "bg-red-50" },
                ].map((st, i) => (
                  <div key={i} className={`p-3 rounded-xl ${st.bg} cursor-pointer hover:shadow-sm transition-all`}
                    onClick={() => { if (i > 1) { const f = ["", "", "completed", "pending", "overdue"][i]; openPanel("compliance", `Compliance — ${st.label}`, `${st.value} assignments`); setPanel(prev => ({ ...prev, filter: f })); } }}
                    data-testid={`comp-stat-${i}`}>
                    <p className="text-lg font-extrabold" style={{ color: st.color }}>{st.value}</p>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{st.label}</p>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Compliance Body Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">

            {/* Left: Template Breakdown + Department Matrix */}
            <div className="p-5 space-y-5">
              {/* Per-Template Breakdown */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-bold text-slate-800">Compliance by Requirement</h3>
                  <InfoTip text="Completion rate per compliance template. Shows which regulatory requirements need attention." />
                </div>
                <div className="space-y-2.5">
                  {(data.compliance_by_template || []).map((t, i) => (
                    <button key={i} className="w-full text-left group" onClick={() => { openPanel("compliance", `Compliance — ${t.title}`, `${t.total} assignments`); setPanel(prev => ({ ...prev, filter: null })); }}
                      data-testid={`comp-template-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-slate-700 truncate max-w-[200px]">{t.title}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          {t.overdue > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{t.overdue} overdue</span>}
                          <span className={`text-xs font-bold ${t.compliance_rate >= 80 ? "text-emerald-600" : t.compliance_rate >= 50 ? "text-amber-600" : "text-red-600"}`}>{t.compliance_rate}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${t.compliance_rate >= 80 ? "bg-emerald-500" : t.compliance_rate >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${t.compliance_rate}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400 w-14 text-right shrink-0">{t.completed}/{t.total}</span>
                      </div>
                    </button>
                  ))}
                  {(data.compliance_by_template || []).length === 0 && <p className="text-xs text-slate-400 text-center py-4">No compliance templates assigned</p>}
                </div>
              </div>

              {/* Department Compliance Matrix */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-bold text-slate-800">Department Compliance</h3>
                  <InfoTip text="Compliance completion rate per department. Red indicates critical gaps needing immediate action." />
                </div>
                <div className="space-y-2">
                  {(data.compliance_by_department || []).map((d, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-all" data-testid={`comp-dept-${i}`}>
                      <div className={`w-2 h-8 rounded-full shrink-0 ${d.severity === "critical" ? "bg-red-500" : d.severity === "warning" ? "bg-amber-500" : "bg-emerald-500"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-semibold text-slate-700 truncate">{d.department}</span>
                          <span className={`text-xs font-bold ${d.severity === "critical" ? "text-red-600" : d.severity === "warning" ? "text-amber-600" : "text-emerald-600"}`}>{d.compliance_rate}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${d.severity === "critical" ? "bg-red-500" : d.severity === "warning" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${d.compliance_rate}%` }} />
                        </div>
                        <div className="flex gap-3 mt-0.5 text-[9px] text-slate-400">
                          <span>{d.completed} done</span>
                          <span>{d.total - d.completed} remaining</span>
                          {d.overdue > 0 && <span className="text-red-500 font-bold">{d.overdue} overdue</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(data.compliance_by_department || []).length === 0 && <p className="text-xs text-slate-400 text-center py-4">No department data</p>}
                </div>
              </div>
            </div>

            {/* Right: Compliance Trend + Upcoming Deadlines + Overdue */}
            <div className="p-5 space-y-5">
              {/* Compliance Trend Mini Chart */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-bold text-slate-800">Compliance Trend</h3>
                  <CalcTip text="Compliance rate = (Completed compliance assignments / Total assigned) x 100. Tracked monthly." />
                </div>
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={data.compliance_trend || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} domain={[0, 100]} />
                    <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 11 }} formatter={(v) => [`${v}%`, "Compliance"]} />
                    <Line type="monotone" dataKey="compliance_rate" stroke="#10b981" strokeWidth={2.5} dot={{ fill: "#10b981", r: 3.5 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Overdue Alerts */}
              {(data.compliance_overdue_details || []).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-bold text-red-600">Overdue Assignments</h3>
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{data.compliance_overdue_details.length}</span>
                  </div>
                  <div className="space-y-1.5">
                    {data.compliance_overdue_details.map((od, i) => (
                      <div key={i} className="flex items-center gap-2.5 p-2 rounded-lg bg-red-50/60 border border-red-100 cursor-pointer hover:bg-red-50 transition-all"
                        onClick={() => od.user_id && navigate(`/tenant/team/${od.user_id}`)} data-testid={`comp-overdue-${i}`}>
                        <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800 truncate">{od.user_name}</p>
                          <p className="text-[10px] text-slate-500 truncate">{od.template_title}</p>
                        </div>
                        <span className="text-[10px] font-bold text-red-600 shrink-0">{od.days_overdue}d late</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Deadlines */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-bold text-slate-800">Upcoming Deadlines</h3>
                  <InfoTip text="Next compliance deadlines across all employees. Critical items are highlighted." />
                </div>
                <div className="space-y-1.5">
                  {(data.compliance_upcoming_deadlines || []).slice(0, 6).map((ud, i) => (
                    <div key={i} className={`flex items-center gap-2.5 p-2 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                      ud.urgency === "critical" ? "border-red-200 bg-red-50/30" : ud.urgency === "warning" ? "border-amber-200 bg-amber-50/30" : "border-slate-100 hover:bg-slate-50"
                    }`}
                      onClick={() => ud.user_id && navigate(`/tenant/team/${ud.user_id}`)} data-testid={`comp-deadline-${i}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-bold ${
                        ud.urgency === "critical" ? "bg-red-100 text-red-700" : ud.urgency === "warning" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                      }`}>{ud.days_left}d</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-800 truncate">{ud.user_name}</p>
                        <p className="text-[10px] text-slate-500 truncate">{ud.template_title}</p>
                      </div>
                      <span className="text-[10px] text-slate-400 shrink-0">{ud.due_date}</span>
                    </div>
                  ))}
                  {(data.compliance_upcoming_deadlines || []).length === 0 && (
                    <div className="text-center py-6">
                      <CheckCircle className="w-6 h-6 text-emerald-300 mx-auto mb-1" />
                      <p className="text-xs text-slate-400">No upcoming deadlines</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </motion.div>

        {/* Row: Dept Breakdown + Category Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div variants={item} className="ent-card p-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="ent-icon-wrap" style={{ background: "rgba(59,130,246,0.08)" }}><Users className="w-5 h-5 text-blue-500" /></div>
              <h2 className="text-base font-bold text-slate-900">Learners by Department</h2>
              <InfoTip text="Distribution of learners across organizational departments." />
            </div>
            {data.department_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.department_breakdown} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} width={100} />
                  <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#3b82f6" radius={[0, 6, 6, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No dept data</div>
            )}
          </motion.div>

          <motion.div variants={item} className="ent-card p-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="ent-icon-wrap" style={{ background: "rgba(245,158,11,0.08)" }}><BookMarked className="w-5 h-5 text-amber-500" /></div>
              <h2 className="text-base font-bold text-slate-900">Top 5 Course Categories</h2>
              <CalcTip text="Categories ranked by total enrollments. Completion rate = (Completed / Enrolled) per category." />
            </div>
            <div className="space-y-3">
              {data.category_data.slice(0, 5).map((cat, i) => (
                <button key={i} className="w-full text-left p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all group"
                  onClick={() => { setPanel({ type: "category", title: cat.name, subtitle: `${cat.course_count} courses · ${cat.enrolled} enrollments`, category: cat }); setPanelSearch(""); }}
                  data-testid={`cat-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-slate-700">{cat.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-blue-600">{cat.completion_rate}%</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${cat.completion_rate}%` }} />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-1.5 text-[10px] text-slate-400">
                    <span>{cat.enrolled} enrolled</span>
                    <span>{cat.completed} completed</span>
                    <span>{cat.in_progress} in progress</span>
                  </div>
                </button>
              ))}
              {data.category_data.length > 5 && (
                <button onClick={() => navigate("/tenant/analytics")} className="w-full text-center py-2.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors" data-testid="view-all-categories">
                  View all {data.category_data.length} categories
                </button>
              )}
              {data.category_data.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">No data</div>}
            </div>
          </motion.div>
        </div>

        {/* Row: Top Courses + Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div variants={item} className="ent-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="ent-icon-wrap" style={{ background: "rgba(139,92,246,0.08)" }}><Target className="w-5 h-5 text-violet-500" /></div>
              <h2 className="text-base font-bold text-slate-900">Top Courses</h2>
              <InfoTip text="Courses ranked by completion count. Click to view in course management." />
            </div>
            <div className="space-y-2.5">
              {data.top_courses?.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-all cursor-pointer group"
                  onClick={() => navigate(`/course/${c.id}`)}>
                  <span className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-700 truncate">{c.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${c.completion_rate}%` }} />
                      </div>
                      <span className="text-[11px] font-bold text-violet-600 w-10 text-right">{c.completion_rate}%</span>
                    </div>
                  </div>
                  {c.avg_score > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 shrink-0">{c.avg_score}%</span>}
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
              {(data.top_courses?.length || 0) > 5 && (
                <button onClick={() => navigate("/tenant/analytics")} className="w-full text-center py-2 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors" data-testid="view-all-courses">
                  View all {data.top_courses.length} courses
                </button>
              )}
            </div>
          </motion.div>

          <motion.div variants={item} className="ent-card overflow-hidden">
            <div className="flex items-center gap-2 p-5 pb-3">
              <div className="ent-icon-wrap" style={{ background: "rgba(16,185,129,0.08)" }}><CheckCircle className="w-5 h-5 text-emerald-500" /></div>
              <h2 className="text-base font-bold text-slate-900">Recent Activity</h2>
              <InfoTip text="Latest enrollment and completion activity across your organization." />
            </div>
            <div className="px-5 pb-5 space-y-2">
              {data.recent_activity?.map((a, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/60 border border-slate-100 hover:border-slate-200 hover:bg-white transition-all cursor-pointer"
                  onClick={() => navigate(a.user_id ? `/tenant/team/${a.user_id}` : `/tenant/team`)}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-xs font-bold ${
                    a.status === "completed" ? "bg-emerald-50 text-emerald-700" : a.status === "in_progress" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"
                  }`}>{a.learner_name.split(" ").map(n => n[0]).join("").slice(0, 2)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{a.learner_name}</p>
                    <p className="text-xs text-slate-500 truncate">{a.activity_text || a.course_title}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`ent-status ent-status-${a.status}`}>
                      <span className={`ent-status-dot ent-status-dot-${a.status}`} />
                      {a.status === "in_progress" ? "In Progress" : a.status === "completed" ? "Done" : a.status === "not_started" ? "Enrolled" : a.status}
                    </span>
                    {a.enrolled_at && <p className="text-[9px] text-slate-400 mt-0.5">{new Date(a.enrolled_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ILT Sessions */}
        {data.upcoming_sessions?.length > 0 && (
          <motion.div variants={item} className="ent-card overflow-hidden">
            <div className="flex items-center justify-between p-5 pb-3">
              <div className="flex items-center gap-2">
                <div className="ent-icon-wrap" style={{ background: "rgba(139,92,246,0.08)" }}><Calendar className="w-5 h-5 text-violet-500" /></div>
                <h2 className="text-base font-bold text-slate-900">Upcoming Sessions</h2>
                <InfoTip text="Scheduled instructor-led training sessions. Click to go to session management." />
              </div>
            </div>
            <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.upcoming_sessions.map((sess, i) => (
                <div key={i} className="p-4 rounded-xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50/30 transition-all cursor-pointer group"
                  onClick={() => navigate("/tenant/sessions")}
                  data-testid={`session-${i}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center group-hover:bg-violet-100 transition-colors"><Play className="w-4 h-4 text-violet-600" /></div>
                    <h4 className="text-sm font-semibold text-slate-800 truncate flex-1">{sess.title}</h4>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-violet-500 transition-colors" />
                  </div>
                  <div className="text-xs text-slate-400 space-y-0.5 ml-11">
                    <p>{sess.session_date} {sess.start_time && `at ${sess.start_time}`}</p>
                    <p>{sess.registered_count || 0} / {sess.max_participants || "N/A"} registered</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Slide Panel */}
      <SlidePanel
        open={!!panel}
        onClose={() => setPanel(null)}
        title={panel?.title}
        subtitle={panel?.subtitle}
      >
        {renderPanelContent()}
      </SlidePanel>
    </AdminLayout>
  );
};

export default TenantAdminDashboard;
